'use client';

/**
 * TransactionFeesCard — shows the org's EFFECTIVE transaction service fees
 * (donation/disbursement/card): the per-org override if set, otherwise the
 * platform default. Read-only; used in Billing & Payments and Org Settings.
 */

import React, { useEffect, useState } from 'react';
import { Percent } from 'lucide-react';
import { getMyTransactionFees, formatTransactionFee, type TransactionFee } from '@/services/api/clientBillingAPI';

export default function TransactionFeesCard({ cboId }: { cboId?: number } = {}) {
  const [fees, setFees] = useState<TransactionFee[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getMyTransactionFees(cboId)
      .then((r) => { if (!cancelled) setFees(r.fees || []); })
      .catch(() => { if (!cancelled) setFees([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [cboId]);

  // Only render when this org has fees explicitly assigned — no fallback/empty
  // card, so billing surfaces stay blank until an operator configures fees.
  if (loading || fees.length === 0) return null;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
      <div className="mb-1 flex items-center gap-2">
        <Percent className="h-4 w-4 text-indigo-600" />
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Transaction fees</h3>
      </div>
      <p className="mb-3 text-sm text-gray-500">
        Service fees applied to money you collect and disburse. Set by your platform operator.
      </p>
      {(
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {fees.map((f) => (
            <div key={f.service_type} className="flex items-center justify-between py-2 text-sm">
              <span className="text-gray-700 dark:text-gray-300">
                {f.label}
                {f.source === 'org_override' && (
                  <span className="ml-2 rounded bg-indigo-50 px-1.5 py-0.5 text-xs text-indigo-700">custom</span>
                )}
              </span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{formatTransactionFee(f)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
